import React, { useState } from 'react';
import PaymentAttemptsList from './PaymentAttemptsList';

function genId(prefix='') { return prefix + Math.random().toString(36).slice(2,10); }

export default function VisaPaymentPanel({ invoice, onUpdateInvoice }) {
  const [amount, setAmount] = useState(invoice?.totals?.total || 0);
  const [currency, setCurrency] = useState('USD');
  const [note, setNote] = useState('');
  const [simulate, setSimulate] = useState('succeeded');

  const attempts = invoice?.paymentAttempts || [];

  function appendAttempt(attempt) {
    const updated = { ...(invoice || {}), paymentAttempts: [...attempts, attempt] };
    onUpdateInvoice && onUpdateInvoice(updated);
  }

  function processPayment(e) {
    e.preventDefault();
    const amt = parseFloat(amount) || 0;
    if (amt <= 0) {
      alert('Amount must be greater than 0');
      return;
    }

    const paymentGroupId = genId('pg-');
    const attempt = {
      attemptId: genId('att-'),
      paymentGroupId,
      invoiceId: invoice?.invoice_id || '',
      method: 'visa',
      amount: parseFloat(amt),
      currency: currency || 'USD',
      status: 'pending',
      failureReason: undefined,
      receiptNumber: undefined,
      createdAt: new Date().toISOString(),
      note: note || ''
    };

    appendAttempt(attempt);

    // resolve after 600ms
    setTimeout(() => {
      const succeeded = simulate === 'succeeded';
      const updatedAttempt = {
        ...attempt,
        status: succeeded ? 'succeeded' : 'failed',
        failureReason: succeeded ? undefined : 'Card declined',
        receiptNumber: succeeded ? `RCT-${Date.now()}` : undefined
      };

      // replace last pending attempt with updated one (append-only semantics preserved by creating new copy)
      const newAttempts = [...(invoice.paymentAttempts || [])];
      // find index of the attempt by attemptId
      const idx = newAttempts.findIndex(a => a.attemptId === attempt.attemptId);
      if (idx !== -1) newAttempts[idx] = updatedAttempt;
      else newAttempts.push(updatedAttempt);

      onUpdateInvoice && onUpdateInvoice({ ...(invoice || {}), paymentAttempts: newAttempts });
    }, 600);
  }

  function handleRetry(failedAttempt) {
    const paymentGroupId = failedAttempt.paymentGroupId || genId('pg-');
    const attempt = {
      attemptId: genId('att-'),
      paymentGroupId,
      invoiceId: invoice?.invoice_id || '',
      method: 'visa',
      amount: failedAttempt.amount,
      currency: failedAttempt.currency || 'USD',
      status: 'pending',
      failureReason: undefined,
      receiptNumber: undefined,
      createdAt: new Date().toISOString(),
      note: failedAttempt.note || ''
    };

    appendAttempt(attempt);

    setTimeout(() => {
      const succeeded = simulate === 'succeeded';
      const updatedAttempt = {
        ...attempt,
        status: succeeded ? 'succeeded' : 'failed',
        failureReason: succeeded ? undefined : 'Card declined',
        receiptNumber: succeeded ? `RCT-${Date.now()}` : undefined
      };

      const newAttempts = [...(invoice.paymentAttempts || [])];
      const idx = newAttempts.findIndex(a => a.attemptId === attempt.attemptId);
      if (idx !== -1) newAttempts[idx] = updatedAttempt;
      else newAttempts.push(updatedAttempt);

      onUpdateInvoice && onUpdateInvoice({ ...(invoice || {}), paymentAttempts: newAttempts });
    }, 600);
  }

  return (
    <div className="grid grid-cols-2 gap-6 items-start">
      <div className="bg-ink/20 border border-edge/50 rounded-hard p-6">
        <h4 className="text-ice font-semibold mb-3">VISA Payment</h4>
        <form onSubmit={processPayment} className="space-y-3">
          <div>
            <label className="block text-mist text-sm mb-1">Amount</label>
            <input type="number" step="0.01" min="0" value={amount} onChange={(e)=>setAmount(e.target.value)} className="w-full bg-ink/40 border border-edge/50 text-ice px-3 py-3 text-sm rounded" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-mist text-sm mb-1">Currency</label>
              <select value={currency} onChange={(e)=>setCurrency(e.target.value)} className="w-full bg-ink/40 border border-edge/50 text-ice px-3 py-2 text-sm rounded">
                <option>USD</option>
              </select>
            </div>
            <div>
              <label className="block text-mist text-sm mb-1">Simulate Result (dev-only)</label>
              <select value={simulate} onChange={(e)=>setSimulate(e.target.value)} className="w-full bg-ink/40 border border-edge/50 text-ice px-3 py-2 text-sm rounded">
                <option value="succeeded">Succeeded</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-mist text-sm mb-1">Note</label>
            <textarea value={note} onChange={(e)=>setNote(e.target.value)} rows={3} className="w-full bg-ink/40 border border-edge/50 text-ice px-3 py-2 text-sm rounded" />
          </div>

          <div className="pt-2">
            <button type="submit" className="bg-violet-600 text-white px-4 py-2 rounded">Process VISA Payment</button>
          </div>
        </form>
      </div>

      <div className="bg-ink/10 border border-edge/40 rounded-hard p-4 h-full">
        <h4 className="text-ice font-semibold mb-3">Payment Attempts</h4>
        <PaymentAttemptsList attempts={attempts} onRetry={handleRetry} />
      </div>
    </div>
  );
}
